import { hasAnalyticsConsent } from "@/lib/cookieConsent";

declare const __APP_VERSION__: string;

let initialized = false;

const ensureSentryPreconnect = () => {
  // Optional perf optimization: we only add it once the user has opted in.
  try {
    if (!document?.head) return;
    const href = "https://o4511010565062656.ingest.de.sentry.io";
    const existing = Array.from(document.head.querySelectorAll("link")).some((l) => l.getAttribute("href") === href);
    if (existing) return;

    const link = document.createElement("link");
    link.rel = "preconnect";
    link.href = href;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);
  } catch {
    // No-op (best-effort)
  }
};

export const ensureSentryInit = async () => {
  if (initialized || !import.meta.env.PROD) return;
  if (!hasAnalyticsConsent()) return;

  initialized = true;

  ensureSentryPreconnect();

  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn: "https://1b7b973eb67d33124e8edfdefef80403@o4511010565062656.ingest.de.sentry.io/4511010871967824",
    release: `equiply@${__APP_VERSION__}`,
    environment: window.location.hostname === "localhost"
      ? "local"
      : window.location.hostname.includes("vercel.app")
        ? "staging"
        : "production",
    integrations: [
      Sentry.browserTracingIntegration(),
      // Valeurs par défaut rendues explicites : la politique de confidentialité promet un
      // enregistrement masqué (textes, saisies et médias).
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
};

export const captureError = async (error: Error, extra?: Record<string, unknown>) => {
  await ensureSentryInit();
  if (!initialized) return;

  const Sentry = await import("@sentry/react");
  Sentry.captureException(error, extra ? { extra } : undefined);
};

export const setSentryUser = async (
  user: { id: string; email?: string; role?: string } | null,
) => {
  await ensureSentryInit();
  if (!initialized) return;

  const Sentry = await import("@sentry/react");
  Sentry.setUser(user);
};

export const setSentryContext = async (
  key: string,
  data: Record<string, unknown> | null,
) => {
  await ensureSentryInit();
  if (!initialized) return;

  const Sentry = await import("@sentry/react");
  Sentry.setContext(key, data);
};
